/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Static-mode replacement for `@salesforce/storefront-next-runtime/i18n`.
 *
 * The real SDK module statically imports `i18next`, `react-i18next`, and
 * `remix-i18next` — so it (and i18next core, ~42 kB raw) lands in the client
 * bundle wherever `getTranslation` is imported (root.tsx + ~35 route/action
 * modules). Aliased in for `I18N_MODE=static` so none of those enter the bundle.
 *
 * Everything here is backed by the synchronous English shim
 * ({@link staticI18nInstance}). This runs on BOTH the server and the client in
 * static mode, which is intentional: an English-only storefront renders SSR HTML
 * from the same dictionary the client hydrates against, so no i18next instance,
 * async resource loading, or per-request middleware is needed.
 *
 * Restore full internationalization by building with `I18N_MODE=dynamic`
 * (default): the alias is not applied and the real SDK module is used again.
 */
import type { MiddlewareFunction, RouterContextProvider } from 'react-router';
import type { InterpolationOptions } from 'i18next';
import { staticI18nInstance, type StaticI18n } from './react-i18next-shim';
import { STATIC_LANGUAGE } from './dictionary';

/**
 * i18next `getTranslation` equivalent. Ignores `context` (English-only) and
 * returns the shared static instance plus its `t`.
 */
export function getTranslation(_context?: Readonly<RouterContextProvider>): {
    i18next: StaticI18n;
    t: StaticI18n['t'];
} {
    return { i18next: staticI18nInstance, t: staticI18nInstance.t };
}

/** Active locale — always English in static mode. */
export function getLocale(_context: Readonly<RouterContextProvider>): string | undefined {
    return STATIC_LANGUAGE;
}

/** Test helper in the real module; inert here. */
export function mockI18nContext(
    _contextProvider: RouterContextProvider,
    _options?: { locale?: string; instance?: unknown }
): void {
    /* inert: static mode has no i18next context to mock */
}

/**
 * The real module returns a lazy remix-i18next middleware that loads locale
 * resources per request. Static English needs none of that: rendering resolves
 * synchronously from the bundled dictionary, so this is a passthrough.
 */
export function createI18nMiddleware(_config: I18nMiddlewareConfig): MiddlewareFunction<Response> {
    return async (_args, next) => next();
}

/** Mirrors the SDK's shared interpolation config (plain object; no i18next dep). */
export const defaultInterpolation: InterpolationOptions = {
    escapeValue: false,
    format: (value: unknown, format?: string) => {
        if (format === 'number' && typeof value === 'number') return value.toLocaleString();
        return value as string;
    },
};

export interface I18nMiddlewareConfig {
    resources: unknown;
    supportedLanguages: string[];
    fallbackLanguage: string;
    interpolation?: InterpolationOptions;
    plugins?: unknown[];
}

export type LocaleLoader = (language: string) => Promise<{ default: unknown }>;
